import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { requireCapability } from '@/lib/auth'
import { lazy,Suspense } from 'react'
import { ImageWithSkeleton } from '@/components/ui/image-with-skeleton'
import type { AxiosError } from '@/lib/api-client'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { api } from '@/lib/api-client'
import { z } from 'zod'
import { useQuery,useMutation,useQueryClient,type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTheme } from 'next-themes'
import { formatDistanceToNow } from 'date-fns'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Card,CardContent,CardDescription,CardHeader,CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
X,
Plus,
MoreVertical,
MoreHorizontal,
Pencil,
Trash2,Clock,
Search,
Info,
Eye,
RefreshCw
} from 'lucide-react'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip,TooltipContent,TooltipProvider,TooltipTrigger } from '@/components/ui/tooltip'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { queryKeys } from '@/lib/query-keys'
import { extractArray } from '@/features/http'
import { useSelectedShopId } from '@/features/shared'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'
import { formatInStoreTz } from '@/lib/time'

const AddEditClassificationDialog = lazy(() =>
  import('@/features/catalog/dialogs/add-edit-classification-dialog').then(m => ({
    default: m.AddEditClassificationDialog,
  }))
)

const AddEditCategoryDialog = lazy(() =>
  import('@/features/catalog/dialogs/add-edit-category-dialog').then(m => ({
    default: m.AddEditCategoryDialog,
  }))
)

interface Classification {
  id: string
  name: string
  slug: string
  description?: string
  organizationId?: string | null
  isMj?: boolean
  createdAt?: string
  updatedAt?: string
  categories?: Category[]
}

interface Category {
  id: string
  classificationId: string
  name: string
  slug: string
  colorCode?: string
  details?: string
  metrcCategoryStringId?: string
  metrcPurchaseCategoryStringId?: string
  images?: string[]
  organizationId?: string | null
}

type PaginatedData<T> = {
  data: T[]
  meta: {
    page: number
    totalPages: number
    totalItems: number
  }
}

type OptimisticPaginatedContext<T> = {
  previousData: [QueryKey, PaginatedData<T> | undefined][]
}

// Snapshot restored by the category mutations' rollback (matches what their
// onMutate actually returns — the OptimisticArrayContext shape never did).
type CategoryMutationContext = {
  previousClassifications: [QueryKey, unknown][]
  previousClassification: unknown
}

type CategoryResponse = {
  id?: string
  data?: {
    id?: string
  }
}

type UomAssignment = {
  uomId?: string
  id?: string
}

type UomResponse = UomAssignment[] | {
  data?: UomAssignment[]
}

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(10),
  search: z.string().catch(''),
})

export const Route = createFileRoute('/_dashboard/catalog/classifications')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  validateSearch: searchSchema,
  component: ClassificationsLayout,
})

let suppressedClassificationsAutoOpenSignature: string | null = null

function ClassificationsLayout() {
  return <Outlet />
}

export function ClassificationsPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, search: searchQuery } = useSearch({ from: Route.id })
  const shopId = useSelectedShopId()
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${searchQuery}`
  const queryClient = useQueryClient()
  const { resolvedTheme } = useTheme()

  // Local state for UI
  const [searchInput, setSearchInput] = React.useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [editingClassification, setEditingClassification] = React.useState<Classification | null>(null)
  const [shouldFocusDescription, setShouldFocusDescription] = React.useState(false)
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = React.useState(false)
  const [isEditingCategory, setIsEditingCategory] = React.useState(false)
  const [isViewOnlyCategory, setIsViewOnlyCategory] = React.useState(false)
  const [categoryToDelete, setCategoryToDelete] = React.useState<Category | null>(null)
  const [classificationToDelete, setClassificationToDelete] = React.useState<Classification | null>(null)

  // Category Form
  const [catFormData, setCatFormData] = React.useState<Partial<Category>>({
    name: '',
    details: '',
    colorCode: '#4CAF50',
    images: [],
  })
  const [, setCatErrors] = React.useState<{ name?: string }>({})
  const [selectedUomIds, setSelectedUomIds] = React.useState<string[]>([])

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only a changed term resets to page 1 — on mount the term is unchanged,
      // so reloads and shared links keep their ?page=.
      navigate({ to: '.', search: (prev) => ((prev.search ?? '') === searchInput ? prev : { ...prev, search: searchInput, page: 1 }), replace: true })
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, navigate])

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/catalog/classifications/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }

        navigate({ to: '/catalog/classifications', search: (prev) => ({ ...prev }) })
    },
    [navigate]
  )

  // Queries
  const classificationsQuery = useQuery({
    queryKey: [...queryKeys.classifications(shopId), { page, limit, search: searchQuery }],
    queryFn: async () => {
      const response = await api.get<{ data?: Classification[]; meta?: { page?: number; totalPages?: number; total?: number } }>('/classifications', {
        params: { page, limit, search: searchQuery || undefined },
      })
      const data = extractArray<Classification>(response)
      // The API returns `meta.total` (like the products endpoint). Normalize it to
      // `totalItems` so the pagination count and optimistic updates read it correctly.
      const meta = {
        page: response.meta?.page ?? page,
        totalPages: response.meta?.totalPages ?? 1,
        totalItems: response.meta?.total ?? data.length,
      }
      return { data, meta }
    },
    enabled: !!shopId,
  })

  const uomsQuery = useQuery({
    queryKey: queryKeys.uoms(),
    queryFn: async () => {
      const response = await api.get<{ id: string; name: string; shortForm: string }[]>('/units-of-measurement')
      return extractArray<{ id: string; name: string; shortForm: string }>(response)
    },
  })

  // Since we don't have direct access to classifications list easily from paginated query for finding the selected item if it's not on the current page,
  // we do a secondary query to fetch it directly if needed.
  const selectedClassificationQuery = useQuery({
    queryKey: queryKeys.classification(selectedId),
    queryFn: async () => {
      if (!selectedId) return null
      const response = await api.get<Classification>(`/classifications/${selectedId}`)
      return response as Classification | null
    },
    enabled: !!selectedId,
  })

  const classifications = classificationsQuery.data?.data || []
  const currentPageHasSelectedClassification =
    !!selectedId && classifications.some((classification) => classification.slug === selectedId || classification.id === selectedId)
  const effectiveSelectedId = (() => {
    if (currentPageHasSelectedClassification) return selectedId
    if (classificationsQuery.isLoading || classifications.length === 0) return undefined
    if (!selectedId && suppressedClassificationsAutoOpenSignature === pageSignature) return undefined
    return classifications[0].slug || classifications[0].id
  })()
  const selectedClassification = classifications.find(
    (c) => c.slug === effectiveSelectedId || c.id === effectiveSelectedId
  ) || selectedClassificationQuery.data || null

  // Get categories directly from the selected classification instead of fetching again
  const categoriesData = selectedClassification?.categories || []
  const isCategoriesLoading = classificationsQuery.isLoading || selectedClassificationQuery.isLoading

  const closeSelectedClassification = React.useCallback(() => {
    suppressedClassificationsAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/catalog/classifications')) {
          suppressedClassificationsAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  // Mutations
  const saveClassification = useMutation<void, unknown, Partial<Classification>, OptimisticPaginatedContext<Classification>>({
    mutationFn: async (payload: Partial<Classification>) => {
      if (isEditing && editingClassification) {
        await api.patch(`/classifications/${editingClassification.id}`, payload)
      } else {
        await api.post('/classifications', payload)
      }
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.classifications(shopId) })
      const previousData = queryClient.getQueriesData<PaginatedData<Classification>>({ queryKey: queryKeys.classifications(shopId) })

      queryClient.setQueriesData<PaginatedData<Classification>>(
        { queryKey: queryKeys.classifications(shopId) },
        (old) => {
          if (!old) return old

          if (isEditing && editingClassification) {
            return {
              ...old,
              data: old.data.map((item) =>
                item.id === editingClassification.id ? { ...item, ...payload } : item
              ),
            }
          }

          const tempClassification: Classification = {
            id: `temp-${Date.now()}`,
            name: payload.name || '',
            slug: payload.name || '',
            description: payload.description,
            isMj: payload.isMj,
          }

          return {
            ...old,
            data: old.meta.page === 1 ? [tempClassification, ...old.data] : old.data,
            meta: { ...old.meta, totalItems: old.meta.totalItems + 1 },
          }
        }
      )

      return { previousData }
    },
    onSuccess: () => {
      toast.success(`Classification ${isEditing ? 'updated' : 'created'} successfully`)
      handleCloseDialog(false)
    },
    onError: (error: unknown, _payload, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error((error as AxiosError<{ message?: string }>).response?.data?.message || 'Failed to save classification')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classifications(shopId) })
    },
  })

  const deleteClassification = useMutation<void, unknown, string, OptimisticPaginatedContext<Classification>>({
    mutationFn: async (id: string) => {
      await api.delete(`/classifications/${id}`)
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.classifications(shopId) })
      const previousData = queryClient.getQueriesData<PaginatedData<Classification>>({ queryKey: queryKeys.classifications(shopId) })

      queryClient.setQueriesData<PaginatedData<Classification>>(
        { queryKey: queryKeys.classifications(shopId) },
        (old) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.filter((item) => item.id !== id),
            meta: { ...old.meta, totalItems: Math.max(0, old.meta.totalItems - 1) },
          }
        }
      )

      return { previousData }
    },
    onSuccess: (_, deletedId) => {
      if (selectedId === deletedId) setSelectedId(null)
      toast.success('Classification deleted successfully')
    },
    onError: (error: unknown, _id, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error('Failed to delete classification', { description: (error as AxiosError<{ message?: string }>).response?.data?.message })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classifications(shopId) })
    },
  })

  const saveCategory = useMutation<void, unknown, Partial<Category>, CategoryMutationContext>({
    mutationFn: async (payload: Partial<Category>) => {
      let savedCategoryId = catFormData.id
      if (isEditingCategory && catFormData.id) {
        await api.patch(`/categories/${catFormData.id}`, payload)
      } else {
        const res = await api.post<CategoryResponse>('/categories/', payload)
        savedCategoryId = res?.id || res?.data?.id
      }

      if (savedCategoryId && selectedUomIds.length > 0) {
        const uomPayload = { uoms: selectedUomIds.map((id) => ({ uomId: id, isDefault: false })) }
        await api.post(`/categories/${savedCategoryId}/uoms`, uomPayload)
      }
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.classifications(shopId) })
      await queryClient.cancelQueries({ queryKey: queryKeys.classification(selectedId) })

      const previousClassifications = queryClient.getQueriesData({ queryKey: queryKeys.classifications(shopId) })
      const previousClassification = queryClient.getQueryData(['classification', selectedId])

      const updateCategories = (categories: Category[] = []): Category[] => {
        if (isEditingCategory && catFormData.id) {
          return categories.map((cat) => (cat.id === catFormData.id ? { ...cat, ...payload } : cat)) as Category[]
        }
        const tempCategory: Category = {
          id: `temp-${Date.now()}`,
          classificationId: payload.classificationId || selectedId || '',
          name: payload.name || '',
          slug: payload.name || '',
          colorCode: payload.colorCode,
          details: payload.details,
          images: payload.images,
        }
        return [tempCategory, ...categories]
      }

      queryClient.setQueriesData<PaginatedData<Classification>>(
        { queryKey: queryKeys.classifications(shopId) },
        (old) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.map((c) =>
              c.id === selectedId || c.slug === selectedId || (selectedClassification && c.id === selectedClassification.id)
                ? { ...c, categories: updateCategories(c.categories) }
                : c
            ),
          }
        }
      )

      queryClient.setQueryData<Classification>(['classification', selectedId], (old) => {
        if (!old) return old
        return { ...old, categories: updateCategories(old.categories) }
      })

      return { previousClassifications, previousClassification }
    },
    onSuccess: () => {
      toast.success(`Category ${isEditingCategory ? 'updated' : 'created'} successfully`)
      handleCloseCategoryDialog(false)
    },
    onError: (error: unknown, _payload, context) => {
      if (context?.previousClassifications) {
        for (const [key, data] of context.previousClassifications) {
          queryClient.setQueryData(key, data)
        }
      }
      if (context?.previousClassification) {
        queryClient.setQueryData(['classification', selectedId], context.previousClassification)
      }
      toast.error((error as AxiosError<{ message?: string }>).response?.data?.message || 'Failed to save category')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classifications(shopId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.classification(selectedId) })
    },
  })

  const deleteCategory = useMutation<void, unknown, string, CategoryMutationContext>({
    mutationFn: async (id: string) => {
      await api.delete(`/categories/${id}`)
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.classifications(shopId) })
      await queryClient.cancelQueries({ queryKey: queryKeys.classification(selectedId) })

      const previousClassifications = queryClient.getQueriesData({ queryKey: queryKeys.classifications(shopId) })
      const previousClassification = queryClient.getQueryData(['classification', selectedId])

      queryClient.setQueriesData<PaginatedData<Classification>>(
        { queryKey: queryKeys.classifications(shopId) },
        (old) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.map((c) =>
              c.id === selectedId || c.slug === selectedId || (selectedClassification && c.id === selectedClassification.id)
                ? { ...c, categories: (c.categories || []).filter((cat) => cat.id !== id) }
                : c
            ),
          }
        }
      )

      queryClient.setQueryData<Classification>(['classification', selectedId], (old) => {
        if (!old) return old
        return { ...old, categories: (old.categories || []).filter((cat) => cat.id !== id) }
      })

      return { previousClassifications, previousClassification }
    },
    onSuccess: () => {
      toast.success('Category deleted successfully')
    },
    onError: (error: unknown, _id, context) => {
      if (context?.previousClassifications) {
        for (const [key, data] of context.previousClassifications) {
          queryClient.setQueryData(key, data)
        }
      }
      if (context?.previousClassification) {
        queryClient.setQueryData(['classification', selectedId], context.previousClassification)
      }
      toast.error('Failed to delete category', { description: (error as AxiosError<{ message?: string }>).response?.data?.message })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.classifications(shopId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.classification(selectedId) })
    },
  })

  const fetchCategoryUoms = async (categoryId: string) => {
    try {
      const response = await api.get<UomResponse>(`/categories/${categoryId}/uoms`)
      const assigned = Array.isArray(response) ? response : Array.isArray(response.data) ? response.data : []
      setSelectedUomIds(assigned.map((u: UomAssignment) => u.uomId || u.id).filter((id): id is string => Boolean(id)))
    } catch {
      setSelectedUomIds([])
    }
  }

  // Handlers
  const handleCloseDialog = (open: boolean) => {
    setIsAddDialogOpen(open)
    if (!open) {
      setTimeout(() => {
        setIsEditing(false)
        setEditingClassification(null)
        setShouldFocusDescription(false)
      }, 300)
    }
  }

  const handleCloseCategoryDialog = (open: boolean) => {
    setIsCategoryDialogOpen(open)
    if (!open) {
      setTimeout(() => {
        setCatFormData({ name: '', details: '', colorCode: '#4CAF50', images: [] })
        setCatErrors({})
        setIsEditingCategory(false)
        setSelectedUomIds([])
        setIsViewOnlyCategory(false)
      }, 300)
    }
  }

  const handleEdit = (item: Classification, focusDescription = false) => {
    setIsEditing(true)
    setEditingClassification(item)
    setIsAddDialogOpen(true)
    setShouldFocusDescription(focusDescription)
  }

  const handleEditCategory = async (item: Category, viewOnly = false) => {
    setCatFormData({
      id: item.id,
      name: item.name,
      details: item.details || '',
      colorCode: item.colorCode || '#4CAF50',
      images: item.images || [],
    })
    setIsEditingCategory(true)
    setIsViewOnlyCategory(viewOnly)
    await fetchCategoryUoms(item.id)
    setIsCategoryDialogOpen(true)
  }

  const handleSaveCategory = (data: {
    classificationId: string
    name: string
    details?: string
    colorCode?: string
    images?: string[]
    uomIds: string[]
  }) => {
    if (!selectedId) return
    if (!data.name?.trim()) {
      setCatErrors({ name: 'Name is required' })
      return
    }
    setSelectedUomIds(data.uomIds)
    setCatFormData({
      ...catFormData,
      name: data.name,
      details: data.details,
      colorCode: data.colorCode,
      images: data.images,
    })
    saveCategory.mutate({
      classificationId: selectedClassification?.id || selectedId || '',
      name: data.name,
      details: data.details,
      colorCode: data.colorCode,
      images: data.images,
    })
  }

  const totalItems = classificationsQuery.data?.meta?.totalItems || classifications.length
  const totalPages = classificationsQuery.data?.meta?.totalPages || 1

  return (
    <div className="space-y-6">
      <PageBreadcrumb />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Classifications</h2>
          <p className="text-muted-foreground">Manage product classifications, MJ status, and categories.</p>
        </div>
        {(classifications.length > 0 || classificationsQuery.isLoading) && (
          <Button
            onClick={() => {
              setIsEditing(false)
              setIsAddDialogOpen(true)
            }}
            className="transition-all hover:scale-105 hover:shadow-md"
          >
            <Plus className="mr-0.5 h-4 w-4" /> Add Classification
          </Button>
        )}
      </div>

      <Suspense fallback={null}>
        {isAddDialogOpen && (
          <AddEditClassificationDialog
            open={isAddDialogOpen}
            onClose={() => handleCloseDialog(false)}
            onSave={(data) => saveClassification.mutate(data)}
            isPending={saveClassification.isPending}
            isEditing={isEditing}
            initialData={
              isEditing && editingClassification
                ? {
                    name: editingClassification.name,
                    description: editingClassification.description || '',
                    isMj: editingClassification.isMj || false,
                  }
                : undefined
            }
            shouldFocusDescription={shouldFocusDescription}
          />
        )}
      </Suspense>

      {(classifications.length > 0 || searchQuery || classificationsQuery.isLoading) && (
        <div className="flex items-center gap-2 max-w-sm w-full">
          <div className="relative flex-1 group">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search classifications..."
              className="pl-9 pr-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchInput('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Refresh"
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true)
              void classificationsQuery.refetch()
              setTimeout(() => setIsRefreshing(false), 600)
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`flex-1 min-w-0 transition-all duration-300 ${!classificationsQuery.isLoading && classifications.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden'} ${selectedClassification ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]' : 'w-full'}`}>
          {!classificationsQuery.isLoading && classifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
              <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                <img src="/misc/logo.png" alt="Rootpoint Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col gap-4 items-center">
                <p className="font-medium text-muted-foreground text-lg">No classifications found</p>
                <Button
                  onClick={() => {
                    setIsEditing(false)
                    setIsAddDialogOpen(true)
                  }}
                  className="w-fit"
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Classification
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Classification</TableHead>
                    <TableHead className={`hidden md:table-cell ${selectedClassification ? 'md:hidden xl:table-cell' : ''}`}>Description</TableHead>
                    <TableHead className="w-[80px] text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {classificationsQuery.isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                        <TableCell className="hidden md:table-cell"><Skeleton className="h-5 w-48" /></TableCell>
                        <TableCell><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                      </TableRow>
                    ))
                  ) : (
                    classifications.map((item) => (
                      <TableRow
                        key={item.id}
                        className={`cursor-pointer transition-colors group ${effectiveSelectedId === item.id || effectiveSelectedId === item.slug ? 'bg-muted' : ''}`}
                        onClick={() => {
                          if (item.slug === effectiveSelectedId || item.id === effectiveSelectedId) {
                            closeSelectedClassification()
                            return
                          }
                          setSelectedId(item.slug || item.id)
                        }}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1.5">
                            {item.name}
                            {item.isMj && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <img src="/misc/mj.png" alt="MJ" className="w-4 h-4 object-contain" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Marijuana Product</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          {item.slug && <span className="block whitespace-normal break-all text-xs text-muted-foreground">{item.slug}</span>}
                        </TableCell>
                        <TableCell className={`hidden md:table-cell text-muted-foreground ${selectedClassification ? 'md:hidden xl:table-cell' : ''}`}>
                          {item.description ? (
                            <div className="text-sm">
                              <TooltipProvider delayDuration={1500}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-help">
                                      {item.description.split(' ').length > 7
                                        ? item.description.split(' ').slice(0, 7).join(' ') + ' ...'
                                        : item.description}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-xs break-words">{item.description}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          ) : (
                            <div
                              className={item.organizationId === null ? 'opacity-50 cursor-not-allowed min-w-[110px]' : 'cursor-pointer min-w-[110px]'}
                              onClick={(e) => {
                                if (item.organizationId === null) return
                                e.stopPropagation()
                                handleEdit(item, true)
                              }}
                            >
                              <span className="text-muted-foreground group-hover:hidden whitespace-nowrap">—</span>
                              {item.organizationId !== null && (
                                <span className="text-muted-foreground hidden group-hover:inline text-sm hover:text-foreground transition-colors whitespace-nowrap">Add description</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {item.organizationId === null ? (
                                <DropdownMenuItem disabled className="text-muted-foreground">
                                  System Managed
                                </DropdownMenuItem>
                              ) : (
                                <>
                                  <DropdownMenuItem className="gap-1.5" onClick={(e) => { e.stopPropagation(); handleEdit(item); }}>
                                    <Pencil className="h-4 w-4" />Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className="gap-1.5 text-destructive focus:text-destructive" onClick={(e) => { e.stopPropagation(); setClassificationToDelete(item); }}>
                                    <Trash2 className="h-4 w-4" />Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <DataTablePagination
                  page={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={limit}
                  entityName="classifications"
                  isLoading={classificationsQuery.isLoading}
                  onPageChange={(p: number) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })}
                  onLimitChange={(l: number) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })}
                />
            </>
          )}
        </div>

        {selectedClassification && (
          <div className="squircle w-full md:w-[320px] lg:w-[360px] sticky top-0 flex flex-col gap-3 h-fit max-h-[calc(100vh-6.5rem)] overflow-y-auto animate-in slide-in-from-right-10 fade-in duration-300 pb-6 pt-1 -mt-1 px-2 -mx-2">
            <Card className="shadow-lg shrink-0">
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 bg-card z-10">
                <div className="grid gap-0">
                  <CardTitle className="text-xl flex items-center gap-1.5 leading-tight">
                    {selectedClassification.name}
                    {selectedClassification.isMj && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <img src="/misc/mj.png" alt="MJ" className="w-5 h-5 object-contain" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Marijuana Product</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {selectedClassification.organizationId === null && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help mt-[1px]" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>This classification is managed by the system</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </CardTitle>
                  <CardDescription className="font-mono text-xs -mt-0.5">{selectedClassification.slug}</CardDescription>
                </div>
                <div className="flex items-center gap-1 -mt-1">
                  {selectedClassification.organizationId !== null && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground"
                        >
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-32">
                        <DropdownMenuItem
                          onClick={() => handleEdit(selectedClassification)}
                          className="gap-1.5 cursor-pointer"
                        >
                          <Pencil className="h-4 w-4" />Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setClassificationToDelete(selectedClassification)}
                          className="gap-1.5 text-red-500 hover:text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 cursor-pointer"
                        >
                          <Trash2 className="h-4 w-4" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closeSelectedClassification}
                    className="h-9 w-9 transition-all hover:bg-muted hover:scale-105 duration-200"
                  >
                    <X className="h-5 w-5" />
                    <span className="sr-only">Close</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 overflow-y-auto pb-0 px-6">
                <div className="space-y-1">
                  <h4 className="font-medium leading-none">Description</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {selectedClassification.description || (
                      <span
                        className={selectedClassification.organizationId === null ? 'text-muted-foreground opacity-50 cursor-not-allowed inline-block' : 'text-muted-foreground cursor-pointer hover:text-foreground transition-colors inline-block scale-x-150'}
                        title={selectedClassification.organizationId === null ? 'System managed' : 'Add description'}
                        onClick={() => {
                          if (selectedClassification.organizationId === null) return
                          handleEdit(selectedClassification, true)
                        }}
                      >
                        —
                      </span>
                    )}
                  </p>
                </div>

                {selectedClassification.organizationId !== null && (
                  <div className="pt-2 mt-2 border-t border-border/50 grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                    <div>
                      <span className="font-medium text-foreground block mb-1">Created</span>
                      {selectedClassification.createdAt ? formatInStoreTz(selectedClassification.createdAt, 'MMM d, yyyy') : 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium text-foreground block mb-1">Updated</span>
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        {selectedClassification.updatedAt ? formatDistanceToNow(new Date(selectedClassification.updatedAt), { addSuffix: true }) : 'N/A'}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="px-4 !pt-0 !pb-1 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <h4 className="font-medium leading-none">Categories</h4>
                    <span className="text-xs text-muted-foreground mt-1 leading-none">Related categories</span>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setIsEditingCategory(false)
                      setIsCategoryDialogOpen(true)
                    }}
                    className="h-6 w-6"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>

              <Suspense fallback={null}>
                {isCategoryDialogOpen && selectedClassification && (
                  <AddEditCategoryDialog
                    open={isCategoryDialogOpen}
                    onClose={() => handleCloseCategoryDialog(false)}
                    onSave={handleSaveCategory}
                    isPending={saveCategory.isPending}
                    classificationName={selectedClassification.name}
                    isEditing={isEditingCategory}
                    viewOnly={isViewOnlyCategory}
                    initialData={{
                      ...catFormData,
                      classificationId: selectedClassification?.id || selectedId || '',
                      uomIds: selectedUomIds,
                    }}
                    uomOptions={uomsQuery.data || []}
                    isLoadingUoms={uomsQuery.isLoading}
                  />
                )}
              </Suspense>
              <CardContent className="p-0">
                {isCategoriesLoading ? (
                  <div className="flex flex-col">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 border-b last:border-0">
                        <Skeleton className="w-10 h-10 rounded-full shrink-0" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : categoriesData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center bg-muted/20">
                    <p className="text-sm text-muted-foreground font-medium">No categories found.</p>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {categoriesData.map((bg) => (
                      <div key={bg.id} className="flex items-center gap-3 pb-3 border-b last:border-0 last:pb-0 hover:bg-muted/50 p-2 transition-colors duration-200">
                         <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border bg-muted/20">
                          <ImageWithSkeleton
                            src={
                              bg.images && bg.images.length > 0 && bg.images[0]
                                ? bg.images[0]
                                : resolvedTheme === 'dark'
                                  ? '/placeholder-dark/Place%20Holder%20Dark%204_4.png'
                                  : '/placeholder-light/Place%20Holder%20Light%204_4.png'
                            }
                            alt={bg.name}
                            wrapperClassName="w-full h-full"
                            className="w-full h-full object-cover"
                          />
                         </div>
                        <div className="flex gap-2 flex-1 justify-between min-w-0">
                          <div className="grid gap-1">
                            <div className="font-semibold text-sm flex items-center gap-1.5">
                              {bg.name}
                              {bg.colorCode && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: bg.colorCode }} />}
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2">{bg.details || 'No details provided.'}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {bg.organizationId === null ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                                onClick={() => handleEditCategory(bg, true)}
                              >
                                <Eye className="h-3 w-3" />
                                <span className="sr-only">View Category</span>
                              </Button>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground">
                                    <MoreVertical className="h-3 w-3" />
                                    <span className="sr-only">Open menu</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem className="gap-1.5" onClick={() => handleEditCategory(bg)}>
                                    <Pencil className="h-3.5 w-3.5" />Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="gap-1.5 text-destructive focus:text-destructive"
                                    onClick={() => setCategoryToDelete(bg)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <DeleteConfirmationDialog
        open={!!categoryToDelete}
        onOpenChange={(open: boolean) => !open && setCategoryToDelete(null)}
        title="Delete Category?"
        description={
          <>
            This action cannot be undone. This will permanently delete the category{' '}
            <span className="font-medium text-foreground">"{categoryToDelete?.name}"</span> and
            remove it from our servers.
          </>
        }
        onConfirm={() => {
          if (categoryToDelete) {
            deleteCategory.mutate(categoryToDelete.id)
            setCategoryToDelete(null)
          }
        }}
        confirmLabel="Delete Category"
        isPending={deleteCategory.isPending}
      />

      <DeleteConfirmationDialog
        open={!!classificationToDelete}
        onOpenChange={(open: boolean) => !open && setClassificationToDelete(null)}
        title="Delete Classification?"
        description={
          <>
            This action cannot be undone. This will permanently delete the classification{' '}
            <span className="font-medium text-foreground">"{classificationToDelete?.name}"</span>{' '}
            and all its associated categories.
          </>
        }
        onConfirm={() => {
          if (classificationToDelete) {
            deleteClassification.mutate(classificationToDelete.id)
            setClassificationToDelete(null)
          }
        }}
        confirmLabel="Delete Classification"
        isPending={deleteClassification.isPending}
      />
    </div>
  )
}
